/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView } from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import * as Layout from '@components/screen/Layout';
import MarketTypeSearch from '@components/form/search/MarketTypeSearch';
import PaymentTermsSearch from '@components/form/search/PaymentTermsSearch';
import { isWeb, isMobileWeb, isAndroid, fullHeight } from '@global/screens';
import { showPopup } from '@/store/modal';
import { BOOLEAN_STATUS, AUTOMATE_CREDIT_LIMIT } from '@/constants';
import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';

const FormValidationSchema = Yup.object().shape({
  name: Yup.string().required('name is required.'),
  description: Yup.string().required('description is required.'),
  is_active: Yup.boolean(),
  market_type: Yup.object().required('Market Type is required.'),
  payment_terms: Yup.object().required('Payment Terms is required.'),
  automate_credit_limit: Yup.string().required(
    'Automate credit limit is required.',
  ),
});

const CoaTaxSettingFormScreen = (props) => {
  const dispatch = useDispatch();
  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  const getCreditLimitDetail = useApi(MsAccount.getCreditLimitDetail);

  const getDetail = async (id) => {
    const response = await getCreditLimitDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      setDetailData(result);
    }
  };

  const goBack = () => {
    // Edit → back to the detail it was opened from; add → back to the list.
    if (isEdit) {
      props.navigation.navigate('CreditLimitDetailScreen', props.route.params);
      return;
    }
    props.navigation.replace('CreditLimitFormScreen', {});
    props.navigation.navigate('CreditLimitScreen');
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {isEdit && getCreditLimitDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={{
            name: isEdit && detailData?.name ? detailData.name : '',
            description:
              isEdit && detailData?.description ? detailData.description : '',
            is_active:
              isEdit && detailData?.is_active ? detailData.is_active : false,
            market_type:
              isEdit && detailData?.market_type ? detailData.market_type : '',
            payment_terms:
              isEdit && detailData?.payment_terms
                ? detailData.payment_terms
                : '',
            automate_credit_limit:
              isEdit && detailData?.automate_credit_limit
                ? detailData.automate_credit_limit
                : 'not_set',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              const finalPayload = {
                name: payload.name,
                description: payload.description,
                is_active: payload.is_active,
                market_type: {
                  id: payload.market_type?.id,
                  code: payload.market_type?.code,
                },
                payment_terms: {
                  id: payload.payment_terms?.id,
                  name: payload.payment_terms?.name,
                },
                automate_credit_limit: payload.automate_credit_limit,
              };

              if (!isEdit) {
                const createData = await MsAccount.createCreditLimit(
                  finalPayload,
                );
                if (createData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  props.navigation.navigate('CreditLimitDetailScreen', {
                    id: createData?.data?.data?._id,
                    name: createData?.data?.data?.name,
                    titleScreen: createData?.data?.name?.name,
                  });
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              } else {
                const updateData = await MsAccount.updateCreditLimit(
                  detailData._id,
                  finalPayload,
                );
                if (updateData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  props.navigation.navigate('CreditLimitScreen', {
                    id: updateData?.data?.data?._id,
                    name: updateData?.data?.data?.name,
                    titleScreen: updateData?.data?.data?.name,
                  });
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
              dispatch(showPopup({ name: 'isLoading', value: false }));
            } catch (error) {
              console.log(error);
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => (
            <>
              <Layout.Wrapper white={false}>
                <Layout.Back
                  title="Customer Credit Limit"
                  onCallback={() => goBack()}
                />
                <Layout.Title
                  title={`${isEdit ? `Edit` : `Add`} Customer Credit Limit`}>
                  <View style={styles.row}>
                    <Button
                      type="default"
                      rounded
                      buttonStyle={{ width: 120, marginRight: 10 }}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.black,
                          }}>
                          Cancel
                        </SociollaText>
                      }
                      onPress={goBack}
                    />
                    <Button
                      type="primary"
                      rounded
                      buttonStyle={{ width: 120 }}
                      title={
                        <SociollaText
                          type="bold"
                          style={{
                            fontSize: 14,
                            letterSpacing: 0.5,
                            color: Colors.white,
                          }}>
                          {isEdit ? 'SAVE' : 'ADD'}
                        </SociollaText>
                      }
                      onPress={handleSubmit}
                    />
                  </View>
                </Layout.Title>
              </Layout.Wrapper>
              <Layout.Wrapper rounded>
                <ScrollView
                  style={isMobileWeb ? { height: fullHeight - 55 } : {}}>
                  <Layout.FormWrapper
                    style={{
                      paddingHorizontal: 15,
                      paddingTop: 20,
                      ...(isWeb
                        ? {
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            paddingHorizontal: 20,
                            marginBottom: 15,
                          }
                        : {
                            paddingBottom: !isMobileWeb
                              ? isAndroid
                                ? 150
                                : 80
                              : 100,
                          }),
                    }}>
                    <View style={styles.rowForm}>
                      <Layout.FormGroup>
                        <Input
                          label="Name"
                          value={values.name}
                          isRequired
                          placeholder="Name"
                          hasError={
                            errors.name && touched.name ? errors.name : null
                          }
                          onChangeText={(val) => {
                            setFieldValue('name', val);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          label="Description"
                          value={values.description}
                          isRequired
                          placeholder="Description"
                          hasError={
                            errors.description && touched.description
                              ? errors.description
                              : null
                          }
                          onChangeText={(val) => {
                            setFieldValue('description', val);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SelectBox
                          label="Status"
                          options={BOOLEAN_STATUS}
                          value={
                            values.is_active
                              ? BOOLEAN_STATUS[0]
                              : BOOLEAN_STATUS[1]
                          }
                          placeholder="Select status"
                          trackBy="value"
                          labelKey="name"
                          isRequired
                          hasError={
                            errors.is_active && touched.is_active
                              ? errors.is_active
                              : null
                          }
                          onChange={(value) => {
                            setFieldValue('is_active', value.value);
                          }}
                        />
                      </Layout.FormGroup>
                    </View>
                    <View style={styles.rowForm}>
                      <Layout.FormGroup>
                        <MarketTypeSearch
                          value={values.market_type}
                          isRequired={true}
                          hasError={
                            errors.market_type && touched.market_type
                              ? errors.market_type
                              : null
                          }
                          onChange={(value) =>
                            setFieldValue('market_type', value)
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <PaymentTermsSearch
                          value={values.payment_terms}
                          isRequired={true}
                          hasError={
                            errors.payment_terms && touched.payment_terms
                              ? errors.payment_terms
                              : null
                          }
                          onChange={(value) =>
                            setFieldValue('payment_terms', value)
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SelectBox
                          label="Automate Credit Limit"
                          options={AUTOMATE_CREDIT_LIMIT}
                          value={
                            values.automate_credit_limit
                              ? AUTOMATE_CREDIT_LIMIT.find(
                                  (val) =>
                                    val.value == values.automate_credit_limit,
                                )
                              : ''
                          }
                          placeholder="Select Automate Credit Limit"
                          trackBy="value"
                          labelKey="label"
                          isRequired
                          hasError={
                            errors.automate_credit_limit &&
                            touched.automate_credit_limit
                              ? errors.automate_credit_limit
                              : null
                          }
                          hideSearch
                          onChange={(value) => {
                            setFieldValue('automate_credit_limit', value.value);
                          }}
                        />
                      </Layout.FormGroup>
                    </View>
                  </Layout.FormWrapper>
                </ScrollView>
              </Layout.Wrapper>
            </>
          )}
        </Formik>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
});

export default CoaTaxSettingFormScreen;
